import React, { useContext, useState, useEffect } from "react";
import "./styles.scss";
import MainContext from "../../Contexts/MainContext";
import Clock from "../../Hooks/useClock";
import ModalRegistrarPonto from "../ModalRegistrarPonto";
import CalendarWidget from "../CalendarWidget";
import TimeNotifier from "../TimeNotifier";
import AdminRender from "../AdminRender";
import { ALL_PONTO_COLABORADOR } from "../../Services/Endpoints";
import api from "../../Services/api";
import { GetAllFeriados, GetAllColaboradores } from "../../Services/ApiCalls";
import { BsCalendar } from "react-icons/bs";
import { IoMdAlert } from "react-icons/io";
import { FiLogOut } from "react-icons/fi";
import ModalCrud from "../ModalCrud";
interface UserRenderProps {
    info: any;
}

const UserRender: React.FC<UserRenderProps> = ({ info }) => {
    const { currentLoggedUserId, token, removeToken } = useContext(MainContext);
    const { dataAllFeriados } = GetAllFeriados(token);
    const { dataAllColaboradores } = GetAllColaboradores(token);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [pontos, setPontos] = useState<any[]>([]);
    const [periodMode, setPeriodMode] = useState<"neste" | "anterior">("neste");
    const [currentTime, setCurrentTime] = useState(new Date());

    // Fetch points
    const fetchPontos = async () => {
        try {
            const resp = await api.get(`${ALL_PONTO_COLABORADOR}/${currentLoggedUserId}`, {
                headers: { Authorization: token },
            });
            if (resp.status === 200 && resp.data) {
                setPontos(resp.data);
            }
        } catch (err) {
            console.error("Erro ao buscar pontos do colaborador", err);
        }
    };

    useEffect(() => {
        fetchPontos();
        
        // Timer for real-time progress update
        const intervalId = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000); // 1 minute
        
        return () => clearInterval(intervalId);
    }, [currentLoggedUserId]);

    // Helper para converter "DD/MM/YYYY" para Date
    const parseDateStr = (dateStr: string) => {
        if(!dateStr) return new Date(0);
        if(dateStr.includes("/")) {
            const [d, m, y] = dateStr.split("/");
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d), 12, 0, 0);
        }
        return new Date(0);
    };

    // Helper para converter "HH:mm" ou "HH:mm:ss" para minutos
    const timeToMinutes = (timeStr: string) => {
        if(!timeStr) return 0;
        const [h, m] = timeStr.split(":");
        return parseInt(h) * 60 + parseInt(m);
    };

    const minutesToDisplay = (totalMins: number) => {
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}min`;
    };

    const calculateTodayHours = () => {
        const today = currentTime;
        const todayStr = today.toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit', year: 'numeric' });
        
        const todayPontos = pontos
            .filter((p: any) => p.data === todayStr)
            .sort((a, b) => timeToMinutes(a.horario) - timeToMinutes(b.horario));

        let workedMins = 0;
        let almocoMins = 0;

        const nowMins = today.getHours() * 60 + today.getMinutes();

        if (todayPontos.length === 1) {
            workedMins += nowMins - timeToMinutes(todayPontos[0].horario);
        } else if (todayPontos.length >= 2) {
            workedMins += timeToMinutes(todayPontos[1].horario) - timeToMinutes(todayPontos[0].horario);
        }
        
        if (todayPontos.length === 3) {
            almocoMins = timeToMinutes(todayPontos[2].horario) - timeToMinutes(todayPontos[1].horario);
            workedMins += nowMins - timeToMinutes(todayPontos[2].horario);
        } else if (todayPontos.length >= 4) {
            almocoMins = timeToMinutes(todayPontos[2].horario) - timeToMinutes(todayPontos[1].horario);
            workedMins += timeToMinutes(todayPontos[3].horario) - timeToMinutes(todayPontos[2].horario);
        }

        // Identify if there is an extra time today
        const extraPonto = todayPontos.find((p: any) => p.tipo && p.tipo.toLowerCase().includes('extra'));
        const hasExtra = !!extraPonto;

        return {
            worked: minutesToDisplay(workedMins),
            almoco: minutesToDisplay(almocoMins),
            todayPontos,
            hasExtra,
            inInterval: todayPontos.length === 2 || todayPontos.length === 6
        };
    };

    const { worked: workedToday, todayPontos, inInterval, hasExtra } = calculateTodayHours();

    // Calcula período
    const getPeriodDates = () => {
        const date = new Date();
        if (periodMode === "neste") {
            const start = new Date(date.getFullYear(), date.getMonth(), 1);
            const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            return { start, end };
        } else {
            const start = new Date(date.getFullYear(), date.getMonth() - 1, 1);
            const end = new Date(date.getFullYear(), date.getMonth(), 0);
            return { start, end };
        }
    };

    const { start: pStart, end: pEnd } = getPeriodDates();
    const periodStr = `${pStart.toLocaleDateString("pt-BR", {day:'2-digit', month:'2-digit', year:'numeric'})} a ${pEnd.toLocaleDateString("pt-BR", {day:'2-digit', month:'2-digit', year:'numeric'})}`;

    const calculatePeriodStats = () => {
        const periodPontos = pontos.filter((p: any) => {
            const pd = parseDateStr(p.data);
            return pd >= pStart && pd <= pEnd;
        });

        // Agrupar por data para contar dias trabalhados e horas
        const byDate: Record<string, any[]> = {};
        periodPontos.forEach((p) => {
            if(!byDate[p.data]) byDate[p.data] = [];
            byDate[p.data].push(p);
        });

        const diasTrabalhados = Object.keys(byDate).length;
        
        let totalWorkedMins = 0;
        Object.values(byDate).forEach(dayPontos => {
            dayPontos.sort((a, b) => timeToMinutes(a.horario) - timeToMinutes(b.horario));
            let dailyMins = 0;
            if (dayPontos.length >= 2) dailyMins += timeToMinutes(dayPontos[1].horario) - timeToMinutes(dayPontos[0].horario);
            if (dayPontos.length >= 4) dailyMins += timeToMinutes(dayPontos[3].horario) - timeToMinutes(dayPontos[2].horario);
            
            // Apply holiday bonus if applicable
            if (dayPontos.length > 0 && dataAllFeriados && Array.isArray(dataAllFeriados)) {
                const dateStr = dayPontos[0].data; // "DD/MM/YYYY"
                const [d, m, y] = dateStr.split("/");
                const searchDate = `${y}-${m}-${d}`;
                const foundHoliday = dataAllFeriados.find((f: any) => f.data === searchDate);
                if (foundHoliday) {
                    const aliquotaStr = String(foundHoliday.aliquotaAcrecimo || '100').replace('%', '');
                    const aliquota = Number(aliquotaStr);
                    if (!isNaN(aliquota)) {
                        dailyMins += (dailyMins * aliquota) / 100;
                    }
                }
            }

            totalWorkedMins += dailyMins;
        });

        // Faltas (Simulação: dias úteis no período até hoje - dias trabalhados)
        // Isso é uma aproximação. Idealmente vem do back.
        let faltas = 0; 
        
        return {
            diasTrabalhados,
            horasRegistradas: minutesToDisplay(totalWorkedMins),
            faltas: "0", // Simbólico
            atrasos: "0"  // Simbólico
        };
    };

    const stats = calculatePeriodStats();

    // Helper to check if there are incomplete past days (Red status)
    const checkPendingPastDays = () => {
        const today = new Date(currentTime);
        today.setHours(0, 0, 0, 0);

        const hasPending = pontos.some(p => {
            if (!p.data) return false;
            const [d, m, y] = p.data.split('/');
            const pDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));

            if (pDate.getTime() < today.getTime()) {
                const dayPontos = pontos.filter((dp: any) => dp.data === p.data);
                const hasEntrada = dayPontos.some((dp: any) => dp.tipo && dp.tipo.toLowerCase().includes('entrada'));
                const hasSaida = dayPontos.some((dp: any) => dp.tipo && (dp.tipo.toLowerCase().includes('saída') || dp.tipo.toLowerCase().includes('saida')) && !dp.tipo.toLowerCase().includes('almoço') && !dp.tipo.toLowerCase().includes('intervalo'));

                if (!(hasEntrada && hasSaida)) {
                    return true;
                }
            }
            return false;
        });

        return hasPending ? 'red' : 'green';
    };    // Data por extenso
    const dateOptions: any = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const todayFullStr = new Date().toLocaleDateString('pt-BR', dateOptions);

    // Extrai primeiro nome
    const getFirstName = () => {
        let nameToUse = info.nome;
        let emailToUse = info.username || '';
        
        const rawUser = emailToUse || '';
        const userPart = rawUser.split('@')[0].split('.')[0];
        
        return nameToUse
            ? nameToUse.split(' ')[0]
            : userPart
                ? userPart.charAt(0).toUpperCase() + userPart.slice(1)
                : 'Colaborador';
    };

    const firstName = getFirstName();

    // Calcular progresso diário (assumindo 8h = 480min meta)
    const workedMinsForProgress = timeToMinutes(workedToday.replace('h', ':').replace('min', ''));
    const progressPercent = Math.min((workedMinsForProgress / 480) * 100, 100);
    const strokeDasharray = 251.2; // 2 * pi * 40 (r=40 na viewBox de 100)
    const strokeDashoffset = strokeDasharray - (strokeDasharray * progressPercent) / 100;

    return (
        <div className="new-dashboard__wrapper">
            <div className="new-dashboard__top">
                <div className="top__left">
                    <h1 className="greeting">Olá, {firstName}!</h1>
                    <p className="current-date">{todayFullStr}</p>

                    <div className="clock-section">
                        <span className="clock-label">Horário atual</span>
                        <div className="clock-display">
                            <Clock />
                        </div>
                    </div>

                    <button className="btn-registrar" onClick={() => setIsModalOpen(true)}>
                        Registrar Ponto
                    </button>

                    <div className="mini-log">
                        <div className="mini-log-title">RESUMO DO DIA</div>
                        <div className="mini-log-cards">
                            {todayPontos.map((p: any, idx: number) => {
                                const isExtra = p.tipo?.toLowerCase().includes('extra');
                                return (
                                    <div key={idx} className={`mini-log-card ${isExtra ? 'extra' : ''}`}>
                                        <span className="card-type">{p.tipo ? p.tipo.toUpperCase() : 'PONTO'}</span>
                                        <span className="card-time">{p.horario}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="top__center" style={{ flex: 1, minWidth: '300px', maxWidth: '400px' }}>
                    <CalendarWidget pontos={pontos} onRefresh={fetchPontos} />
                </div>

                <div className="top__right">
                    <div className="user-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                        <button 
                            className="action-btn action-logout" 
                            onClick={() => setIsLogoutModalOpen(true)}
                            title="Sair do Sistema"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 16px', color: '#ef4444', cursor: 'pointer', fontWeight: 500 }}
                        >
                            <FiLogOut size={20} />
                            <span>Sair</span>
                        </button>
                    </div>
                    <div className="circle-stats">
                        <svg className="progress-ring" viewBox="0 0 100 100">
                            <circle className="progress-ring__bg" cx="50" cy="50" r="40" />
                            <circle
                                className="progress-ring__circle"
                                cx="50" cy="50" r="40"
                                strokeDasharray={strokeDasharray}
                                strokeDashoffset={strokeDashoffset}
                            />
                        </svg>
                        <div className="circle-content">
                            <h2>{workedToday}</h2>
                            <p>TRABALHADO HOJE</p>
                            {hasExtra && <span className="extra-time-label">HORA EXTRA</span>}
                        </div>
                    </div>
                </div>
            </div>

            <div className="new-dashboard__bottom">
                <div className="period-info">
                    <span>Período: {periodStr}</span>
                </div>
                <div className="stats-cards">
                    <div className="stat-card">
                        <p>DIAS TRABALHADOS</p>
                        <h3>{stats.diasTrabalhados}</h3>
                    </div>
                    <div className="stat-card">
                        <p>HORAS REGISTRADAS</p>
                        <h3>{stats.horasRegistradas}</h3>
                    </div>
                    <div className="stat-card">
                        <p>FALTAS</p>
                        <h3>{stats.faltas}</h3>
                    </div>
                    <div className="stat-card">
                        <p>ATRASOS</p>
                        <h3>{stats.atrasos}</h3>
                    </div>
                </div>

                <div className="period-toggles">
                    <button 
                        className={periodMode === "neste" ? "active" : ""} 
                        onClick={() => setPeriodMode("neste")}
                    >
                        <BsCalendar style={{marginRight: 8}}/> Neste período
                    </button>
                    <button 
                        className={periodMode === "anterior" ? "active" : ""} 
                        onClick={() => setPeriodMode("anterior")}
                    >
                        <BsCalendar style={{marginRight: 8}}/> Período anterior
                    </button>
                </div>
            </div>

            {isModalOpen && (
                <ModalRegistrarPonto 
                    pontos={pontos}
                    onClose={() => setIsModalOpen(false)} 
                    onSuccess={() => {
                        setIsModalOpen(false);
                        fetchPontos();
                    }}
                />
            )}

            {isLogoutModalOpen && (
                <ModalCrud onClose={() => { setIsLogoutModalOpen(false); return {}; }}>
                    <div className="logout__modal" style={{ padding: '20px', textAlign: 'center' }}>
                        <h3 style={{ marginTop: 0 }}>Sair do sistema</h3>
                        <p style={{ color: '#64748b' }}>Tem certeza que deseja encerrar sua sessão?</p>
                        <div className="logout__actions" style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '20px' }}>
                            <button 
                                onClick={() => setIsLogoutModalOpen(false)}
                                style={{ padding: '10px 20px', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={() => {
                                    removeToken("token");
                                }}
                                style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                Sim, sair
                            </button>
                        </div>
                    </div>
                </ModalCrud>
            )}

            <TimeNotifier 
                pontos={pontos} 
                onOpenModal={() => setIsModalOpen(true)} 
            />
        </div>
    );
};

export default UserRender;
